import React, { useEffect, useState, useCallback, FormEvent, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import {
  AppstoreOutlined,
  AuditOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  ClearOutlined,
  FileTextOutlined,
} from '@ant-design/icons';

import { getAllQuestions } from '../../../../redux/api/questionsApi';
import { QuestionApiType } from '../../types';
import { QuestionList } from '../QuestionList';
import {
  BoxButtonsStyled,
  ButtonStyled,
  ButtonStyledThree,
  ButtonStyledTwo,
  ColStyled,
  ColStyledTwo,
  FormStyled,
  LabelStyled,
  RowStyled,
  SkeletonField,
} from './styles';
import { getFamilies, getModulesbyFamilies } from 'redux/api/question';
import { Select } from '../Select';
import { Input } from '../Input';
import { notification } from 'antd';
import { buildQuestionRoute } from '../../utils/buildQuestionRoute';

const STORAGE_KEY = 'new-questions-filters';

interface StoredFilters {
  term: string;
  families: string;
  modules: string;
  question_type: string;
  page: number;
}

const getStoredFilters = (): StoredFilters | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

const saveFiltersToStorage = (filters: StoredFilters) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore
  }
};

const clearStoredFilters = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

export const SearchQuestion: React.FC = () => {
  const [widthScreen, setWidthScreen] = useState(() => window.innerWidth);
  const history = useHistory();
  const shouldReloadAfterClear = useRef(false);
  // Sequência da última busca disparada. Mais de uma carga pode estar no ar ao
  // mesmo tempo (carga inicial + troca de página, por exemplo) e as respostas não
  // voltam necessariamente na ordem: sem isto, a resposta antiga chega depois e
  // sobrescreve a lista e o total de páginas da busca atual.
  const lastRequestId = useRef(0);
  const [loading, setLoading] = useState({
    questions: true,
    filters: true,
    modules: true,
  });
  const [openFilters, setOpenFilters] = useState(false);
  const [questions, setQuestions] = useState<QuestionApiType[]>([]);
  const [families, setFamilies] = useState([]);
  const [modules, setModules] = useState([]);
  const [filters, setFilters] = useState(() => {
    const stored = getStoredFilters();
    return {
      term: stored?.term || '',
      families: stored?.families || '',
      modules: stored?.modules || '',
      question_type: stored?.question_type || '',
      page: stored?.page || 1,
      totalPages: 0,
    };
  });
  const [currentPage, setCurrentPage] = useState(() => {
    const stored = getStoredFilters();
    return stored?.page || 1;
  });

  const loadQuestions = useCallback(
    async (overrides: { page?: number } = {}) => {
      const requestId = ++lastRequestId.current;

      setLoading((prev) => ({ ...prev, questions: true }));
      try {
        const mergedFilters = { ...filters, ...overrides };
        const sendFilters = Object.fromEntries(
          Object.entries(mergedFilters).filter(([_, v]) => v !== ''),
        );
        let data = await getAllQuestions(sendFilters);

        // Resposta de uma busca que já foi substituída: descarta.
        if (requestId !== lastRequestId.current) {
          return;
        }

        if (data && data.data.length === 0 && data.current_page > 1) {
          saveFiltersToStorage({
            term: mergedFilters.term,
            families: mergedFilters.families,
            modules: mergedFilters.modules,
            question_type: mergedFilters.question_type,
            page: 1,
          });
          data = await getAllQuestions({ ...sendFilters, page: 1 });

          if (requestId !== lastRequestId.current) {
            return;
          }
        }
        if (data) {
          const { current_page, last_page } = data;
          setQuestions(data.data);
          setCurrentPage(current_page);
          setFilters((prev) => ({ ...prev, page: current_page, totalPages: last_page }));
        } else {
          notificationError(new Error('getAllQuestions retornou null'));
          setQuestions([]);
        }
      } catch (error) {
        if (requestId !== lastRequestId.current) {
          return;
        }

        notificationError(error);
        setQuestions([]);
      } finally {
        if (requestId === lastRequestId.current) {
          setLoading((prev) => ({ ...prev, questions: false }));
        }
      }
    },
    [filters],
  );

  const loadFilters = useCallback(async () => {
    setLoading((prev) => ({ ...prev, filters: true, questions: true }));
    try {
      const data = await getFamilies();
      if (data) {
        setFamilies(data);
      }
    } catch (error) {
      notificationError(error);
      setFamilies([]);
    } finally {
      setLoading((prev) => ({ ...prev, filters: false, questions: false }));
    }
  }, []);

  const loadModules = useCallback(async (familie_id: string) => {
    setLoading((prev) => ({ ...prev, modules: true }));
    try {
      const data = await getModulesbyFamilies(familie_id);
      if (data) {
        setModules(data[familie_id]);
      }
    } catch (error) {
      notificationError(error);
      setModules([]);
    } finally {
      setLoading((prev) => ({ ...prev, modules: false }));
    }
  }, []);

  const notificationError = (error: any) => {
    console.error('Erro ao carregar questões:', error);
    notification.error({
      message: 'Erro ao carregar questões',
      description: 'Não foi possível carregar as questões. Tente novamente.',
    });
  };

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      // Uma busca nova sempre volta para a primeira página. A página vinha do
      // filtro guardado (localStorage): buscar estando na página 2 pedia a página
      // 2 do termo novo e a lista voltava vazia mesmo havendo resultado.
      setCurrentPage(1);
      setFilters((prev) => ({ ...prev, page: 1 }));
      saveFiltersToStorage({
        term: filters.term,
        families: filters.families,
        modules: filters.modules,
        question_type: filters.question_type,
        page: 1,
      });
      // `page: 1` explícito: `filters` da closure ainda tem a página anterior.
      loadQuestions({ page: 1 });
    },
    [loadQuestions, filters],
  );

  const handleClearForm = useCallback(() => {
    clearStoredFilters();
    shouldReloadAfterClear.current = true;
    setFilters({
      term: '',
      families: '',
      modules: '',
      question_type: '',
      page: 1,
      totalPages: 0,
    });
    setCurrentPage(1);
    setModules([]);
  }, []);

  const handleQuestionClick = (questionId: string, questionStepId?: number | null) => {
    history.push(buildQuestionRoute(questionId, questionStepId));
  };

  const handleChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const setFieldValue = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));

    if (field === 'families' && !value) {
      setFilters((prev) => ({ ...prev, modules: '' }));
    }
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
    setFilters((prev) => {
      const newFilters = { ...prev, page };
      saveFiltersToStorage({
        term: newFilters.term,
        families: newFilters.families,
        modules: newFilters.modules,
        question_type: newFilters.question_type,
        page: newFilters.page,
      });
      return newFilters;
    });
    loadQuestions({ page });
  };

  useEffect(() => {
    const initializeFilters = async () => {
      await loadFilters();
      const stored = getStoredFilters();
      if (stored?.families) {
        await loadModules(stored.families);
      }
      loadQuestions();
    };
    initializeFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleResize = () => setWidthScreen(window.innerWidth);
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (widthScreen < 768) {
      setOpenFilters(false);
    } else {
      setOpenFilters(true);
    }
  }, [widthScreen]);

  useEffect(() => {
    if (shouldReloadAfterClear.current) {
      shouldReloadAfterClear.current = false;
      loadQuestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.term, filters.families, filters.modules, filters.question_type]);

  return (
    <FormStyled onSubmit={(e) => handleSubmit(e)}>
      <RowStyled>
        <ColStyled>
          <LabelStyled htmlFor="term">Enunciado ou código</LabelStyled>
          {loading.filters ? (
            <SkeletonField height={36} marginTop="0px" />
          ) : (
            <Input
              value={filters.term}
              onChange={(value) => handleChange('term', value)}
              placeholder="Insira aqui..."
            />
          )}
        </ColStyled>
        {openFilters && (
          <>
            <ColStyled>
              {loading.filters ? (
                <SkeletonField height={36} marginTop="26px" />
              ) : (
                <Select
                  value={filters.question_type}
                  onChange={(value) => {
                    setFieldValue('question_type', value);
                  }}
                  options={questionsType.map((type: { value: string; label: string }) => ({
                    value: type.value,
                    label: type.label,
                  }))}
                  placeholder="Selecione um tipo..."
                  placeholderIcon={<FileTextOutlined color="#d6d6d6" />}
                />
              )}
            </ColStyled>
            <ColStyled>
              {loading.filters ? (
                <SkeletonField height={36} marginTop="26px" />
              ) : (
                <Select
                  value={filters.families}
                  onChange={(value) => {
                    setFieldValue('families', value);
                    loadModules(value);
                  }}
                  options={families.map((family: { familie_id: string; name: string }) => ({
                    value: family.familie_id,
                    label: family.name,
                  }))}
                  placeholder="Selecione uma família..."
                  placeholderIcon={<AppstoreOutlined color="#d6d6d6" />}
                />
              )}
            </ColStyled>
            <ColStyled>
              {(loading.modules && filters.families.length > 0) || loading.filters ? (
                <SkeletonField height={36} marginTop="26px" />
              ) : (
                <Select
                  value={filters.modules}
                  onChange={(value) => {
                    setFieldValue('modules', value);
                  }}
                  options={
                    modules?.map((module: { id: string; title: string }) => ({
                      value: module.id,
                      label: module.title,
                    })) || []
                  }
                  placeholder="Selecione um módulo..."
                  placeholderIcon={<AuditOutlined color="#d6d6d6" />}
                  disabled={!filters.families}
                />
              )}
            </ColStyled>
          </>
        )}
        <ColStyledTwo>
          <ButtonStyledThree type="button" onClick={() => setOpenFilters(!openFilters)}>
            {openFilters ? (
              <>
                <ArrowUpOutlined /> Menos filtros
              </>
            ) : (
              <>
                <ArrowDownOutlined /> Mais filtros
              </>
            )}
          </ButtonStyledThree>
        </ColStyledTwo>
        <ColStyled>
          <BoxButtonsStyled>
            {loading.filters ? (
              <SkeletonField height={36} marginTop="25px" />
            ) : (
              <ButtonStyled type="submit" disabled={loading.questions}>
                Buscar
              </ButtonStyled>
            )}
            {loading.filters ? (
              <SkeletonField height={36} marginTop="25px" />
            ) : (
              (filters.term !== '' ||
                filters.families !== '' ||
                filters.modules !== '' ||
                filters.question_type !== '') && (
                <ButtonStyledTwo onClick={handleClearForm}>
                  <ClearOutlined /> Limpar
                </ButtonStyledTwo>
              )
            )}
          </BoxButtonsStyled>
        </ColStyled>
      </RowStyled>
      <QuestionList
        loading={loading.questions}
        questions={questions}
        currentPage={currentPage}
        handleQuestionClick={handleQuestionClick}
        handleClearForm={handleClearForm}
        totalPages={filters.totalPages}
        onPageChange={handleChangePage}
      />
    </FormStyled>
  );
};

const questionsType = [
  {
    value: 'decision_tree',
    label: 'Árvore de Decisão',
  },
  {
    value: 'objective_questions',
    label: 'Questões Objetivas',
  },
  {
    value: 'question_context',
    label: 'Questões Contextuais',
  },
  {
    value: 'case_study',
    label: 'Estudo de Caso',
  },
];
